import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import voca from 'voca';

import DestroyModal from 'components/application/DestroyModal';
import RadioField from 'components/application/RadioField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { pluralizeProjectsText } from 'components/projectsDashboard/helpers';

export default function DestroyFolderModal({
  folderId,
  isDestroyDisabled = false,
  onClose,
  onDestroy,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const pluralProjectName =
    currentActor.division.attributes.pluralRAMSName || 'RAMS';

  const [projectsAction, setProjectsAction] = useState('archive');

  const { data: domainFolder } = useQuery({
    queryKey: ['folders', folderId],
    queryFn: async () => {
      const response = await axios.get(`/dashboards/folders/${folderId}`);
      return response.data;
    },
    onError: breadBoard.addInedibleToast,
  });

  if (!domainFolder) return null;

  const projectText = pluralizeProjectsText({
    count: domainFolder.data.meta.projectCount,
    currentActor,
    includeCount: false,
    isCapitalized: false,
  });

  return (
    <DestroyModal
      additionalContent={
        domainFolder.data.meta.projectCount > 0 ?
          <div className='tw-mt-6 tw-flex tw-flex-col tw-gap-4 tw-text-grey-700'>
            <p className='tw-mb-0 tw-text-m tw-font-semibold'>
              What would you like to do with the{' '}
              {pluralizeProjectsText({
                count: domainFolder.data.meta.projectCount,
                currentActor,
                isCapitalized: false,
              })}{' '}
              in this folder?
            </p>
            <RadioField
              checked={projectsAction === 'archive'}
              className='tw-m-0'
              key='archive'
              label={
                <p className='tw-mb-0 tw-text-m tw-font-normal tw-text-grey-700'>
                  Archive {projectText}
                </p>
              }
              name='projects_action'
              onChange={() => setProjectsAction('archive')}
              value='archive'
            />
            <RadioField
              checked={projectsAction === 'division'}
              className='tw-m-0'
              key='division'
              label={
                <>
                  <p className='tw-mb-1 tw-text-m tw-font-normal tw-text-grey-700'>
                    Remove {projectText} from folder
                  </p>
                  <p className='tw-mb-0 tw-text-s tw-font-normal tw-tracking-wide tw-text-grey-500'>
                    Can be accessed from Current{' '}
                    {voca.capitalize(pluralProjectName)}
                  </p>
                </>
              }
              name='projects_action'
              onChange={() => setProjectsAction('division')}
              value='division'
            />
          </div>
        : <p className='tw-mb-0 tw-mt-6 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            There are no {pluralProjectName} in this folder.
          </p>
      }
      confirmationText='Delete folder'
      displayText={`Are you sure you want to delete ${domainFolder.data.attributes.name}?`}
      isDestroyDisabled={isDestroyDisabled}
      isOpen={true}
      onClose={onClose}
      onDestroy={() => {
        onDestroy({
          id: domainFolder.data.id,
          name: domainFolder.data.attributes.name,
          params:
            domainFolder.data.meta.projectCount > 0 ?
              { projects_action: projectsAction }
            : {},
          projectCount: domainFolder.data.meta.projectCount,
        });
      }}
    />
  );
}

DestroyFolderModal.propTypes = {
  folderId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDestroy: PropTypes.func.isRequired,
};
